import { useCallback, useMemo } from 'react';
import { Table, ColumnPinningPosition } from '@tanstack/react-table';
import {
  DragEndEvent,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ColumnReorderKeyboardSensor } from '../utils';

interface UseColumnReorderingProps<T> {
  table: Table<T>;
  enableColumnReordering: boolean;
  disabledColumns: Set<string>;
}

const IMMOVABLE_COLUMN_IDS = ['select', 'actions'];

const restrictToContainerHorizontal: Modifier = ({
  transform,
  scrollableAncestorRects,
  activeNodeRect,
}) => {
  if (!activeNodeRect || scrollableAncestorRects.length === 0)
    return { ...transform, y: 0 };

  const containerRect = scrollableAncestorRects[0];
  const minX = containerRect.left - activeNodeRect.left;
  const maxX = containerRect.right - activeNodeRect.right;

  return {
    ...transform,
    x: Math.min(Math.max(transform.x, minX), maxX),
    y: 0,
  };
};

export const useColumnReordering = <T>({
  table,
  enableColumnReordering,
  disabledColumns,
}: UseColumnReorderingProps<T>) => {
  const getPinnedColumnsWidth = useCallback(
    (side: Exclude<ColumnPinningPosition, false>) =>
      (table.getState().columnPinning[side] ?? []).reduce((total, columnId) => {
        const column = table.getColumn(columnId);

        return total + (column?.getSize() ?? 0);
      }, 0),
    [table]
  );
  const tableState = table.getState();
  const leftPinnedColumnsWidth = getPinnedColumnsWidth('left');
  const rightPinnedColumnsWidth = getPinnedColumnsWidth('right');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(ColumnReorderKeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const visibleLeafColumns = table.getVisibleLeafColumns();

  const reorderableColumnIds = useMemo(() => {
    if (!enableColumnReordering) return [];

    return visibleLeafColumns
      .filter(
        col =>
          !col.getIsPinned() &&
          !IMMOVABLE_COLUMN_IDS.includes(col.id) &&
          !disabledColumns?.has(col.id)
      )
      .map(col => col.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableColumnReordering,
    visibleLeafColumns,
    disabledColumns,
    tableState.columnPinning,
  ]);

  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      const allLeafIds = table.getAllLeafColumns().map(col => col.id);
      const currentOrder = tableState.columnOrder;
      const baseOrder =
        currentOrder.length > 0 ? [...currentOrder] : allLeafIds;

      allLeafIds.forEach(id => {
        if (!baseOrder.includes(id)) baseOrder.push(id);
      });

      const oldIndex = baseOrder.indexOf(activeId);
      const newIndex = baseOrder.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return;

      table.setColumnOrder(arrayMove(baseOrder, oldIndex, newIndex));
    },
    [table, tableState.columnOrder]
  );

  const modifiers = useMemo(() => [restrictToContainerHorizontal], []);

  return useMemo(
    () => ({
      sensors,
      modifiers,
      reorderableColumnIds,
      handleColumnDragEnd,
      leftPinnedColumnsWidth,
      rightPinnedColumnsWidth,
    }),
    [
      sensors,
      modifiers,
      reorderableColumnIds,
      handleColumnDragEnd,
      leftPinnedColumnsWidth,
      rightPinnedColumnsWidth,
    ]
  );
};
